import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PaperPlaneRight } from "@phosphor-icons/react";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Messages() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [convos, setConvos] = useState([]);
  const [messages, setMessages] = useState([]);
  const [other, setOther] = useState(null);
  const [text, setText] = useState("");
  const endRef = useRef(null);

  const loadConvos = useCallback(() => {
    api.get("/conversations").then(({ data }) => setConvos(data)).catch(() => {});
  }, []);

  useEffect(() => { loadConvos(); }, [loadConvos]);

  const loadThread = useCallback(() => {
    if (!userId) return;
    api.get(`/conversations/${userId}/messages`).then(({ data }) => setMessages(data)).catch(() => {});
  }, [userId]);

  useEffect(() => {
    if (!userId) { setOther(null); setMessages([]); return; }
    api.get(`/users`).catch(() => {});
    // resolve other user info from convos or fetch profile via id-less endpoint fallback
    const found = convos.find((c) => c.user.id === userId);
    if (found) setOther(found.user);
    loadThread();
    const t = setInterval(loadThread, 4000);
    return () => clearInterval(t);
  }, [userId, loadThread, convos]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      const { data } = await api.post(`/conversations/${userId}/messages`, { text });
      setMessages([...messages, data]);
      setText("");
      loadConvos();
    } catch {}
  };

  return (
    <div className="max-w-5xl mx-auto px-5 py-8 h-[calc(100vh-4rem)]" data-testid="messages-page">
      <div className="grid md:grid-cols-[300px_1fr] border border-border h-full">
        {/* list */}
        <div className={`border-r border-border overflow-y-auto ${userId ? "hidden md:block" : ""}`}>
          <div className="p-4 border-b border-border">
            <h1 className="font-display text-2xl">Messages</h1>
          </div>
          {convos.length ? convos.map((c) => (
            <button key={c.user.id} data-testid={`convo-${c.user.username}`} onClick={() => navigate(`/messages/${c.user.id}`)}
              className={`w-full flex items-center gap-3 p-4 border-b border-border text-left hover:bg-card transition-colors ${userId === c.user.id ? "bg-card" : ""}`}>
              <div className="h-10 w-10 rounded-full bg-primary/20 border border-primary/40 flex items-center justify-center overflow-hidden shrink-0">
                {c.user.avatar_id ? <img src={fileUrl(c.user.avatar_id)} alt="" className="h-full w-full object-cover" /> : <span className="font-accent font-bold text-sm">{c.user.display_name[0]}</span>}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-accent font-bold text-sm truncate">{c.user.display_name}</p>
                <p className="text-xs text-muted-foreground truncate">{c.last_message}</p>
              </div>
              {c.unread && <span className="h-2 w-2 rounded-full bg-primary shrink-0" />}
            </button>
          )) : <p className="p-4 text-sm text-muted-foreground">No conversations yet. Message a creator from their profile.</p>}
        </div>

        {/* thread */}
        <div className="flex flex-col h-full">
          {userId ? (
            <>
              <div className="p-4 border-b border-border flex items-center gap-3">
                <button className="md:hidden text-muted-foreground" onClick={() => navigate("/messages")}>←</button>
                <p className="font-accent font-bold">{other?.display_name || "Conversation"}</p>
              </div>
              <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-2">
                {messages.map((m) => {
                  const mine = m.from_id === user?.id;
                  return (
                    <div key={m.id} className={`max-w-[75%] px-4 py-2.5 ${mine ? "self-end bg-primary text-primary-foreground" : "self-start bg-card border border-border"}`} data-testid={`msg-${m.id}`}>
                      <p className="text-sm">{m.text}</p>
                    </div>
                  );
                })}
                {messages.length === 0 && <p className="text-center text-muted-foreground text-sm mt-8">Say hello 👋</p>}
                <div ref={endRef} />
              </div>
              <form onSubmit={send} className="p-3 border-t border-border flex gap-2" data-testid="message-form">
                <input data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…"
                  className="flex-1 bg-card border border-border px-4 py-3 focus:border-primary outline-none transition-colors" />
                <button data-testid="message-send" className="px-5 bg-primary text-primary-foreground flex items-center justify-center transition-transform active:scale-95">
                  <PaperPlaneRight size={18} weight="fill" />
                </button>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              <p>Select a conversation</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
